
import { EmailNotificationInterval, EmailNotificationType } from '@/models/Settings';
import useSettings from '@/state/settingsState';
import NotificationSetting from '@/views/settings/components/utils/NotificationSetting.vue';
import { computed, defineComponent, ref } from 'vue';
import useBarChatReminder from '@/views/virtual-pub/composable/bar-chat/useBarChatReminder';
import BCXSearchSelect from '@/components/molecules/BCXSearchSelect.vue';
import useUser from '@/mixins/useUser';

export default defineComponent({
  name: 'SectionNotifications',
  components: {
    NotificationSetting,
    BCXSearchSelect
  },
  setup() {
    const { tUserFallback: t } = useUser('self');
    const {
      isCustomer, isEmployee, isFreelancer, isGuest
    } = useUser('self');

    const showProjectOffersSetting = computed(() => isFreelancer.value || isCustomer.value || isEmployee.value);
    const showProjectMessagesSetting = computed(() => isCustomer.value || isEmployee.value || isGuest.value);
    const showProjectsSection = computed(() => showProjectOffersSetting.value || showProjectMessagesSetting.value);

    const { settings } = useSettings();
    const showAnnouncements = ref(false); // currently hidden

    const {
      isEnabled, isLoading: isBarchatReminderStatusLoading, removeReminder, setReminder
    } = useBarChatReminder();

    const isBarchatReminderEnabled = computed({
      get: () => (isEnabled.value ? 1 : 0),
      set: async (value) => {
        if (isBarchatReminderEnabled.value === value) return;
        if (value === 1) {
          await setReminder();
          isEnabled.value = true;
        } else {
          await removeReminder();
          isEnabled.value = false;
        }
      }
    });

    const barChatReminderList = ref([
      {
        value: 1,
        text: t('settings.notifications.interval_types_pub_opening.YES')
      },
      {
        value: 0,
        text: t('settings.notifications.interval_types_pub_opening.NO')
      }
    ]);

    const getIntervalForType = (type:EmailNotificationType) => settings.value?.notificationSettings.find(
      (notification) => notification.emailNotificationType === type
    ) ?? EmailNotificationInterval.Never;

    const valueRefIncomingMessages = computed(() => getIntervalForType(EmailNotificationType.IncomingMessages) as string);

    const valueRefSubscriptions = computed(() => getIntervalForType(EmailNotificationType.Subscriptions) as string);

    const valueRefRecommendations = computed(() => getIntervalForType(EmailNotificationType.Recommendations) as string);

    const valueRefProjectOffers = computed(() => getIntervalForType(EmailNotificationType.ProjectOffer) as string);

    const valueRefAnnouncements = computed(() => getIntervalForType(EmailNotificationType.Announcements) as string);

    const valueRefNewsletter = computed(() => getIntervalForType(EmailNotificationType.Newsletter) as string);

    return {
      showAnnouncements,
      isCustomer,
      isEmployee,
      isFreelancer,
      isGuest,
      showProjectOffersSetting,
      showProjectMessagesSetting,
      showProjectsSection,
      valueRefAnnouncements,
      valueRefNewsletter,
      valueRefSubscriptions,
      valueRefRecommendations,
      valueRefProjectOffers,
      valueRefIncomingMessages,
      EmailNotificationType,
      isBarchatReminderEnabled,
      isBarchatReminderStatusLoading,
      barChatReminderList,
      t,
    };
  }
});
